import type { MetaDescriptor } from 'react-router';
import type { CreativeWork } from 'schema-dts';

interface ProjectSchemaProps {
    /** Absolute canonical URL of the project page; used as both `@id` and `url`. */
    url: string;
    /** Project title, used as the work's name/headline. */
    name: string;
    /** Plain-text project description. */
    description: string;
    /** Absolute image URLs for the project (hero/social image first). */
    images: string[];
    /** Owning professional's display name; the work's creator/author. */
    professionalName: string;
    /** ISO date the project was published, if known. */
    datePublished?: string;
    /** Primary project category name (e.g. "Kitchen Interiors"). */
    category?: string;
    /** Human-readable location the project was built (e.g. "Auckland, New Zealand"). */
    location?: string;
}

/**
 * Build CreativeWork JSON-LD for an individual project page.
 *
 * Project pages are photo-driven case studies created by a professional, so they map to schema.org
 * CreativeWork with the professional as creator/author and the gallery images as `image`. Emitting this
 * (alongside the existing VideoObject and BreadcrumbList) makes the site's largest indexable surface
 * eligible for richer results and Google Images. Mirrors the shape of `createVideoSchema`: callers resolve
 * the primitives and this returns a single-entry MetaDescriptor[] for `createLDSchema`.
 */
export const createProjectSchema = (
    props: ProjectSchemaProps,
): MetaDescriptor[] => {
    const {
        url,
        name,
        description,
        images,
        professionalName,
        datePublished,
        category,
        location,
    } = props;

    const professional = {
        '@type': 'Organization' as const,
        name: professionalName,
    };

    const projectSchema: CreativeWork = {
        '@type': 'CreativeWork',
        '@id': url,
        url,
        name,
        headline: name,
        description,
        ...(images.length > 0 ? { image: images } : {}),
        creator: professional,
        author: professional,
        ...(datePublished ? { datePublished, dateCreated: datePublished } : {}),
        ...(category ? { about: { '@type': 'Thing', name: category } } : {}),
        ...(location
            ? { locationCreated: { '@type': 'Place', name: location } }
            : {}),
        publisher: {
            '@type': 'Organization',
            name: 'ArchiPro',
        },
    };

    return [projectSchema];
};
